import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api.js";
import { useAsync, useDebounced } from "../lib/hooks.js";
import {
  Empty,
  ErrorState,
  Loading,
  PageIntro,
  StateBadge,
  formatRelative,
} from "../components/ui.jsx";
import { IconRefresh, IconSearch } from "../components/icons.jsx";
import { kindLabel } from "./Dashboard.jsx";

const KINDS = ["BRANCH", "BRANCH_PLUS", "NODEFLEX", "NODENET", "POWERSHARE", "NOVALINK"];
const STATES = ["MANUFACTURED", "ISSUED", "ACTIVE", "SUSPENDED", "DECOMMISSIONED"];

export default function Devices() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("");
  const [state, setState] = useState("");
  const debounced = useDebounced(search, 300);

  const { data, error, loading, reload } = useAsync(
    () => api.listDevices({ q: debounced, kind, state, limit: 200 }),
    [debounced, kind, state]
  );

  const devices = data ?? [];
  const filtered = Boolean(debounced || kind || state);

  return (
    <>
      <PageIntro title="Device registry">
        Every unit issued to your tenant. Select a device to see its channels, send it a
        command, or issue a token. Search matches both the customer-facing number and the
        manufacturer serial.
      </PageIntro>

      <div className="toolbar">
        <div style={{ position: "relative" }}>
          <IconSearch
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-subtle)",
            }}
          />
          <input
            className="input"
            style={{ paddingLeft: 32, minWidth: 260 }}
            placeholder="Search number or serial"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All product kinds</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {kindLabel(k)}
            </option>
          ))}
        </select>

        <select className="select" value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">All states</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0) + s.slice(1).toLowerCase()}
            </option>
          ))}
        </select>

        <span className="spacer" />

        {filtered && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setSearch("");
              setKind("");
              setState("");
            }}
          >
            Clear filters
          </button>
        )}
        <button className="btn btn-secondary btn-sm" onClick={reload}>
          <IconRefresh />
          Refresh
        </button>
      </div>

      {loading ? (
        <Loading label="Loading devices" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : devices.length === 0 ? (
        <div className="card">
          <Empty title={filtered ? "No devices match" : "No devices yet"}>
            {filtered
              ? "Try a broader search, or clear the filters to see the whole estate."
              : "Devices appear here once a production run has been issued to your tenant."}
          </Empty>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Number</th>
                <th>Serial</th>
                <th>Kind</th>
                <th>Channels</th>
                <th>Gateway</th>
                <th>State</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr
                  key={d.Number}
                  className="row-link"
                  onClick={() => navigate(`/devices/${d.Number}`)}
                >
                  <td>
                    <Link className="mono" to={`/devices/${d.Number}`} onClick={(e) => e.stopPropagation()}>
                      {d.Number}
                    </Link>
                  </td>
                  <td className="mono hint">{d.Serial}</td>
                  <td>{kindLabel(d.Kind)}</td>
                  <td>{d.ChannelCount > 1 ? `${d.ChannelCount}-in-1` : "Single"}</td>
                  <td className="mono hint">{d.ParentNumber || "-"}</td>
                  <td>
                    <StateBadge state={d.State} />
                  </td>
                  <td className="hint">{formatRelative(d.LastSeenAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {devices.length >= 200 && (
        <p className="hint" style={{ marginTop: 12 }}>
          Showing the first 200 devices. Narrow the search to see the rest.
        </p>
      )}
    </>
  );
}
